<template>
  <div class="container">
    <div class="row">
      <div class="col">
        <h2 id="agriculture">6. Agriculture</h2>
        <p>
            The Global Food Security Analysis Support Data project (GFSAD30) is an effort to map and produce global cropland data products at 30m resolution [<a href="#ref-9">9</a>, <a href="#ref-10">10</a>]. GFSAD30 is currently one of the only datasets to specifically identify croplands at a high resolution. Croplands are defined as land which is cultivated with plants harvested for food, feed, or fiber. Fallow land which is uncultivated or out of season is also classified as cropland.
        </p>
        <p v-if="loaded">
            The selected area of interest has a total area of <strong>{{ parseInt(this.factsheet.data.measures_global_croplands.cropland_areas.area_total/1000000) }} square kilometers</strong>, and contains 
            
            
            <template v-if="this.factsheet.data.measures_global_croplands.cropland_areas.cropland_fraction < 1">
                <strong>practically no cropland at all</strong>.
            </template>
            <template v-else-if="this.factsheet.data.measures_global_croplands.cropland_areas.cropland_fraction < 5">
                <strong>an extremely small proportion of cropland</strong>.
            </template>
            <template v-else-if="this.factsheet.data.measures_global_croplands.cropland_areas.cropland_fraction < 10">
                <strong>a small proportion of cropland</strong>.
            </template>
            <template v-else-if="this.factsheet.data.measures_global_croplands.cropland_areas.cropland_fraction < 30">
                <strong>a fair proportion of cropland</strong>.
            </template>
            <template v-else-if="this.factsheet.data.measures_global_croplands.cropland_areas.cropland_fraction < 60">
                <strong>a large proportion of cropland</strong>.
            </template>
            <template v-else>
                <strong>a very large proportion of cropland</strong>.
            </template>

            In total, <strong><span v-if="this.factsheet.data.measures_global_croplands.cropland_areas.cropland_fraction < 10">only</span> {{ this.factsheet.data.measures_global_croplands.cropland_areas.cropland_fraction }}% is classified as cropland</strong> with the remaining <strong>{{ 100 - this.factsheet.data.measures_global_croplands.cropland_areas.cropland_fraction }}% classified as non-cropland</strong>. The cropland areas comprise a total of <strong>{{ parseInt(this.factsheet.data.measures_global_croplands.cropland_areas.area_cropland/1000000) }} square kilometers ({{ parseInt(this.factsheet.data.measures_global_croplands.cropland_areas.area_cropland/10000) }} hectare)</strong> and are shown in the map below.
        </p>
      </div>
    </div>

    <div class="row justify-content-md-center">
      <div class="col-lg-12">
        <RasterMap 
          backgrounds="streets,satellite,light,osm" 
          title="High Resolution Cropland Areas" 
          description="Cropland Mask" 
          attribution="Map: Geofolio. Data: GFSAD30/Croplands.org [10]. Backgrounds: Mapbox/Openstreetmap Contributors [13]." 
          v-bind:layout="{legendWidth:3, legendCols:1, descriptionWidth:9 }"
          v-bind:location="location"
          v-bind:factsheet="factsheet"
          v-bind:loaded="loaded"
          v-bind:layers="layers">
          <p>
            Map shows a high-resolution (30m) cropland mask produced by the Global Food Security Analysis Support Data project (GFSAD30) project.
          </p>
          <p>
            Cropland is defined as lands cultivated with plants harvested for food, feed, and fiber, including both seasonal crops (wheat, rice, etc) as well as continuous plantations (coffee, tea, cocoa, etc). Fallow land which is uncultivated for a season is also included in this classification.
            The dataset was created using two machine-learning algorithms and was further refined using Landsat imagery for the period 2013-2015.
          </p>
        </RasterMap>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-12">
        <h3>6.1 Crops</h3>
        <p v-if="loaded">
          There are no global datasets to identify exactly which crops are grown where, especially at the same level of detail as the cropland map above. However, a global dataset by Monfreda et al. [<a href="#ref-11">11</a>] contains harvested areas for various crops at a regional resolution.
        </p>
        <p v-if="loaded">
            <span v-if="cropListPrimary.length > 0">
                In and around the study area, the dataset by Monfreda et al. indicates that the most common crops by (approximate) area harvested are <strong>{{ andList(cropListPrimary) }}</strong>. 
                <span v-if="cropListSecondary.length > 0">
                    Other crops like <strong>{{ andList(cropListSecondary) }}</strong> may also be cultivated in and around this area.
                </span>
            </span>

            <span v-if="cropListPrimary.length == 0">
                In and around the study area, the dataset by Monfreda et al. does not list any crops with a significant area harvested.
            </span>
        </p>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-12">
        <h3>6.2 Crop Calendars</h3>
        <p> 
            Crop calendars give an indication of the growing season of various crops in and around the study area, and specify approximate periods during the year for sowing and harvesting of crops. The crop calendars for this area have been extracted from a global crop calendar dataset by Sacks et al., which combines calendars from USDA, FAO, and several other sources [<a href="#ref-12">12</a>]. 
        </p>

        <div v-if="loaded">

          <p v-if="this.factsheet.data.crop_calendars.crop_calendar.length == 0">
            In this area of interest <strong>no crop calendars were found</strong> in the dataset by Sachs et al.
          </p>
        
          <div v-if="this.factsheet.data.crop_calendars.crop_calendar.length > 0">
              <table class='table table-sm' style="font-size:0.8em;margin-top:15px;" v-if="loaded">
              <thead>
                  <tr><th colspan="4" style="padding-left:0px;border-top:0px;">Table 2. Crop Calendars</th></tr>
                  <tr><th style="padding-left:0px;border:0px;">Location</th><th style="border:0px;">Crop</th><th style="border:0px;">Source</th><th style="text-align:center;border:0px;padding-right:0px;font-weight:400;"><div  style="border:0px;" class="month">Jan</div>
                          <div class="month">Feb</div>
                          <div class="month">Mar</div>
                          <div class="month">Apr</div>
                          <div class="month">May</div>
                          <div class="month">Jun</div>
                          <div class="month">Jul</div>
                          <div class="month">Aug</div>
                          <div class="month">Sep</div>
                          <div class="month">Oct</div>
                          <div class="month">Nov</div>
                          <div class="month">Dec</div>
                  </th></tr>
                  
              </thead>
              <tbody>
                <calendar-item v-for="item in this.factsheet.data.crop_calendars.crop_calendar" v-bind:item="item" :key="item" />
              </tbody>
              <tfoot>
                  <tr><td colspan="4" style="font-size:0.8em;padding:0px;color:#888;">                    
                      <div style="padding:5px 0px 5px 0px;">
                          <span style="width:40px;background-color:#15b01a;display:inline-block;">&nbsp;</span> <span style="padding-right:15px;padding-left:5px;">Planting</span>
                          <span style="width:40px;background-color:#fbeeac;display:inline-block;">&nbsp;</span> <span style="padding-right:15px;padding-left:5px;">Mid-Season</span>
                          <span style="width:40px;background-color:#fac205;display:inline-block;">&nbsp;</span> <span style="padding-right:15px;padding-left:5px;">Harvesting</span>
                      </div>
                  </td></tr>
                  <tr><td colspan="4" style="font-size:0.8em;padding:0px;color:#888;">                    
                      Crop calendars are generally applicable to larger areas such as countries or large agro-ecological zones within a country, and may not be representative for the relatively small area of interest for which this factsheet has been generated.
                  </td></tr>
                  <tr><td colspan="4" style="font-size:0.8em;padding:0px;color:#888;">                    
                      Source: Sacks et al. [<a href="#ref-12">12</a>]
                  </td></tr>


              </tfoot>
              </table>
            </div>
          </div>

      </div>
    </div>


  </div>
</template>

<script>
import _ from 'lodash';
import Vue from 'vue';

import RasterMap from '@/components/factsheet/RasterMap.vue'

const CalendarItem = Vue.component('calendar-item', {
    props: ['item'],
    template: `
        <tr v-if="valid">
          <td style="padding-left:0px;" class="align-middle"><nobr>{{item.location}}</nobr></td>
          <td class="align-middle"><nobr>{{item.crop}}<span v-if="item.qualifier"> ({{item.qualifier}})</span></nobr></td>
          <td class="align-middle">{{item.source}}</td>
          <td style="padding-right:0px;width:50%;" class="align-middle">
            <div style="width:100%;background-color:#f5f5f5;position:relative;height:20px;">
                <div v-bind:style="plant1">&nbsp;</div>
                <div v-bind:style="plant2">&nbsp;</div>
                <div v-bind:style="harvest1">&nbsp;</div>
                <div v-bind:style="harvest2">&nbsp;</div>
                <div v-bind:style="season1">&nbsp;</div>
                <div v-bind:style="season2">&nbsp;</div>
            </div>
          </td>

        </tr>
    `,
    computed: {
        valid: function() {
            if(this.item.plant_start && this.item.plant_end && this.item.harvest_start && this.item.harvest_end) {
                return true
            } else {
                return false
            }
        },
        plant1: function() {
          if(this.item.plant_end > this.item.plant_start) {
            // 1 box
            return "position:absolute;top:0px;left:"+100*(this.item.plant_start/365)+"%;width:"+100*((this.item.plant_end-this.item.plant_start)/365)+"%;background-color:#15b01a;height:20px;"
          } else {
            // 2 boxes
            return "position:absolute;top:0px;left:0%;width:"+100*(this.item.plant_end/365)+"%;background-color:#15b01a;height:20px;"
          }
        },
        plant2: function() {
          if(this.item.plant_end > this.item.plant_start) {
            // 1 box
            return "display:none;"
          } else {
            // 2 boxes
            return "position:absolute;top:0px;left:"+100*(this.item.plant_start/365)+"%;width:"+100*((365-this.item.plant_start)/365)+"%;background-color:#15b01a;height:20px;"
          }
        },
        harvest1: function() {
          if(this.item.harvest_end > this.item.harvest_start) {
            // 1 box
            return "position:absolute;top:0px;left:"+100*(this.item.harvest_start/365)+"%;width:"+100*((this.item.harvest_end-this.item.harvest_start)/365)+"%;background-color:#fac205;height:20px;"
          } else {
            // 2 boxes
            return "position:absolute;top:0px;left:0%;width:"+100*(this.item.harvest_end/365)+"%;background-color:#fac205;height:20px;"
          }
        },
        harvest2: function() {
          if(this.item.harvest_end > this.item.harvest_start) {
            // 1 box
            return "display:none;"
          } else {
            // 2 boxes
            return "position:absolute;top:0px;left:"+100*(this.item.harvest_start/365)+"%;width:"+100*((365-this.item.harvest_start)/365)+"%;background-color:#fac205;height:20px;"
          }
        },
        season1: function() {
          if(this.item.harvest_start == this.item.plant_end) {
            return "display:none;"
          }
          if(this.item.harvest_start > this.item.plant_end) {
            // 1 box
            return "position:absolute;top:0px;left:"+100*(this.item.plant_end/365)+"%;width:"+100*((this.item.harvest_start-this.item.plant_end)/365)+"%;background-color:#fbeeac;height:20px;"
          } else {
            // 2 boxes
            return "position:absolute;top:0px;left:0%;width:"+100*(this.item.harvest_start/365)+"%;background-color:#fbeeac;height:20px;"
          }
        },
        season2: function() {
          if(this.item.harvest_start == this.item.plant_end) {
            return "display:none;"
          }
          if(this.item.harvest_start > this.item.plant_end) {
            // 1 box
            return "display:none;"
          } else {
            // 2 boxes
            return "position:absolute;top:0px;left:"+100*(this.item.plant_end/365)+"%;width:"+100*((365-this.item.plant_end)/365)+"%;background-color:#fbeeac;height:20px;"
          }
        },
    }
});


export default {
    name: 'Agriculture',
    components:{
        RasterMap, CalendarItem
    },
    props: ['location', 'loaded', 'factsheet'],
    data() {
        return {
          layers: [
            {'title': 'Cropland Area', 'source': '/measures_global_croplands/cropland_extent.tif'},
          ]
        }
    },
    created() {
    },
    computed: {
        cropListPrimary: function() {
            var cropListPrimary = []
            _.each(this.factsheet.data.monfreda_crop_areas.harvested_area, area=>{
                if( (area.perc > 10)) {
                    cropListPrimary.push(area['title'])
                }
            });
            return cropListPrimary
        },
        cropListSecondary: function() {
            var cropListSecondary = []
            _.each(this.factsheet.data.monfreda_crop_areas.harvested_area, area=>{
                if( (area.perc < 10) && (area.perc > 2) ) {
                    cropListSecondary.push(area['title'])
                }
            });
            return cropListSecondary
        }
    },
    mounted() {

    },
    methods: {
      andList(list, attr) {
        var ret = ""
        for(var i=0; i<list.length;i++) {
          if (list.length == 1) {
            return list[i]
          }
          if(i == list.length-1) {
            ret += " and "+list[i]
          } else {
            if(list.length > 2) {
              ret += list[i] + ", "
            } else {
              ret += list[i]
            }
          }
        }
        return ret
      }
    }
}
</script>

<style scoped>
    p {
        margin-bottom:5px;
    }
    div.month {
        width:8.333333333%;
        display:inline-block;
        text-align:center;
        border-left:1px solid #ccc;
    }

</style>